import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  agentsData: [],
  editedRows: {},
  loading: false,
  error: null,
  saving: false,
  refreshing: false,
  lastFetched: null,
  selectedAgent: null,
};

export const voiceAIAgentsSlice = createSlice({
  name: 'voiceAIAgents',
  initialState,
  reducers: {
    // Set all agents data
    setAgentsData: (state, action) => {
      state.agentsData = action.payload;
      state.lastFetched = Date.now();
      state.error = null;
    },

    // Add a new agent
    addAgent: (state, action) => {
      const newAgent = action.payload;
      state.agentsData.push(newAgent);
    },

    // Update an existing agent
    updateAgent: (state, action) => {
      const updatedAgent = action.payload;
      const index = state.agentsData.findIndex(a => a.id === updatedAgent.id);
      if (index !== -1) {
        state.agentsData[index] = { ...state.agentsData[index], ...updatedAgent };
      } else {
        state.agentsData.push(updatedAgent);
      }
    },

    // Delete agent
    deleteAgent: (state, action) => {
      const agentId = action.payload;
      state.agentsData = state.agentsData.filter(a => a.id !== agentId);
      if (state.selectedAgent?.id === agentId) {
        state.selectedAgent = null;
      }
    },

    // Track cell edit (before saving to API)
    trackCellEdit: (state, action) => {
      const { agentId, field, value } = action.payload;

      if (!state.editedRows[agentId]) {
        state.editedRows[agentId] = {};
      }

      state.editedRows[agentId][field] = value;

      // Update the local data immediately for UI responsiveness
      const index = state.agentsData.findIndex(a => a.id === agentId);
      if (index !== -1) {
        state.agentsData[index] = {
          ...state.agentsData[index],
          [field]: value
        };
      }
    },

    // Clear all edited rows after successful save
    clearEditedRows: (state) => {
      state.editedRows = {};
    },

    // Clear edited rows for a specific agent
    clearEditedRowsForAgent: (state, action) => {
      const agentId = action.payload;
      delete state.editedRows[agentId];
    },

    // Set selected agent (for detail view)
    setSelectedAgent: (state, action) => {
      state.selectedAgent = action.payload;
    },

    // Set loading state
    setLoading: (state, action) => {
      state.loading = action.payload;
    },

    // Set refreshing state
    setRefreshing: (state, action) => {
      state.refreshing = action.payload;
    },

    // Set saving state
    setSaving: (state, action) => {
      state.saving = action.payload;
    },

    // Set error
    setError: (state, action) => {
      state.error = action.payload;
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },
  },
});

export const {
  setAgentsData,
  addAgent,
  updateAgent,
  deleteAgent,
  trackCellEdit,
  clearEditedRows,
  clearEditedRowsForAgent,
  setSelectedAgent,
  setLoading,
  setRefreshing,
  setSaving,
  setError,
  clearError,
} = voiceAIAgentsSlice.actions;

// Selectors
export const selectAgentsData = (state) => state.voiceAIAgents.agentsData;
export const selectActiveAgents = (state) => state.voiceAIAgents.agentsData.filter(a => a.is_active);
export const selectEditedRows = (state) => state.voiceAIAgents.editedRows;
export const selectSelectedAgent = (state) => state.voiceAIAgents.selectedAgent;
export const selectLoading = (state) => state.voiceAIAgents.loading;
export const selectRefreshing = (state) => state.voiceAIAgents.refreshing;
export const selectSaving = (state) => state.voiceAIAgents.saving;
export const selectError = (state) => state.voiceAIAgents.error;
export const selectLastFetched = (state) => state.voiceAIAgents.lastFetched;

export default voiceAIAgentsSlice.reducer;
